/**
 * THE WORKSPACE SWITCH'S LOOK, PER LIGHT (PV5-8:D part 2, 2026-10-13): the one place its colours, faces and spacing
 * live. The switch overlay's sheet (./workspaceSwitchCss.ts) is written from it, and so is the boot splash's twin of it
 * (./splashTwin.ts), which public/index.html carries verbatim because the splash draws before any of the app has loaded:
 * so the reload gap and the overlay that takes over from it are one screen, the same ground, words and mark in the same
 * places (scripts/check-workspace-switch.mjs measures the handover in a browser).
 *
 * Every colour is a token's, from the token twin (config/design-tokens.json through semanticTokens.generated.ts), as a
 * literal: the splash cannot read a variable. The light is the visitor's own (src/design/previewTheme.ts
 * resolvePreviewTheme: the address's `?theme=`, else their choice, else the device's), the light the pages they land on
 * are drawn in.
 */
import { semanticDark, semanticLight } from '../../../styles/semanticTokens.generated';
import type { MarkTwinInks } from '../../../styles/markSpinnerCss';

export type SwitchLight = 'noon' | 'dusk';

export interface SwitchLook {
  /** The ground: the sky (the canvas token), opaque, so no page shows through. */
  sky: string;
  /** The wordmark, the organization's name, the mark's ink arc. */
  ink: string;
  /** The thread it opens, the usage line, the failure's line. */
  ink2: string;
  /** The eyebrow: the accent's text ink (gold that reads as words on the sky). */
  eyebrow: string;
  /** The usage pill's hairline: the accent. */
  edge: string;
  /** The working mark's inks on this sky: the day's deep satin at noon, the dusk satin at dusk. */
  spin: MarkTwinInks;
  /** The zero's gold: the satin's lit top (its first stop), the day's at noon and the dusk satin's at dusk. */
  satinTop: string;
}

export const SWITCH_LOOKS: Readonly<Record<SwitchLight, SwitchLook>> = {
  noon: {
    sky: semanticLight.canvas,
    ink: semanticLight.ink,
    ink2: semanticLight['ink-2'],
    eyebrow: semanticLight['accent-text'],
    edge: semanticLight.accent,
    // the day's inks of the one spinner (MARK_INKS_ON_LIGHT): the satin's deep end, which reads on a light ground
    spin: { ink: semanticLight.ink, goldHi: semanticLight['gold-lo'], goldBody: semanticLight['gold-edge'], goldLo: semanticLight['gold-edge'] },
    satinTop: semanticLight['gold-sky'],
  },
  dusk: {
    sky: semanticDark.canvas,
    ink: semanticDark.ink,
    ink2: semanticDark['ink-2'],
    eyebrow: semanticDark['accent-text'],
    edge: semanticDark.accent,
    // the dusk satin, as the new design's chat draws the mark at dusk
    spin: { ink: semanticDark.ink, goldHi: semanticDark['gold-sky'], goldBody: semanticDark.accent, goldLo: semanticDark['gold-lo'] },
    satinTop: semanticDark['gold-sky'],
  },
};

/** The new design's face, with the fallbacks the splash also draws in before any font has loaded. */
export const SWITCH_FONT = "'Manrope', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/**
 * The type and the spacing, shared by the overlay and the splash: the wordmark at the splash's size, each line below it
 * by a margin of its own (the splash's continuation draws the same blocks with the same margins). Every line height is
 * set, never inherited: the overlay stands under the app's global sheet (the body's line height) and the splash under
 * none, so an inherited one would put the two wordmarks at different heights.
 */
export const SWITCH_TYPE = {
  wordmark: { size: 'clamp(1.1rem,4.2vw,2rem)', lineHeight: '1.2', weight: 800, tracking: '-.045em' },
  eyebrow: { size: '12.5px', lineHeight: '1.3', weight: 700, tracking: '.12em', marginTop: '14px' },
  org: { size: 'clamp(1.4rem,4vw,1.9rem)', lineHeight: '1.2', weight: 800, tracking: '-.03em', marginTop: '6px' },
  thread: { size: '15px', lineHeight: '1.45', marginTop: '10px' },
  usage: { size: '13px', lineHeight: '1.3', marginTop: '14px', padding: '4px 12px' },
  failed: { size: '15px', lineHeight: '1.45', marginTop: '6px' },
  spin: { marginTop: '18px' },
} as const;

/**
 * The zero in the wordmark: the mark drawn by the one inline mark component (src/components/common/BrandMark.tsx), on
 * the old raster's 545 x 512 canvas with the ring where the old orb was, exactly as the boot splash draws its zero; and
 * sized and placed with the splash's own numbers (public/index.html, `#app-splash .splash-text .splash-zero`). So the
 * overlay's ring is the splash's ring by construction, and scripts/check-workspace-switch.mjs measures it within a pixel.
 */
export const SWITCH_ZERO = { height: '0.95em', width: '1.0112em', margin: '0 -0.05em 0 -0.04em', marginBottom: '-0.08em' } as const;

/** The switch's light for the new design's light. */
export const switchLightOf = (theme: 'light' | 'dark'): SwitchLight => (theme === 'dark' ? 'dusk' : 'noon');
